import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { getActivityAnchorId } from '@aline/common';
import { IconButton, InputBase, Menu, MenuItem, Typography } from '@mui/material';
import {
  ExternalLink,
  Link as LinkIcon,
  MessageCircleMore,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import type { OnSubmitCommentReturn } from 'src/components';
import { AlineAvatar, SmallAlineButton } from 'src/components';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import { normalizeBlankMarkdown } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/empty-paragraph';
import { SlackIcon } from 'src/components/icons/SlackIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  domElIdCreators,
  formatRelativeTime,
  getAssigneeDisplayDetails,
  getOnEnterHandler,
  openLink,
  useDispatchNotification,
} from 'src/helpers';
import { useDeleteComment, useDeleteCommentReply } from 'src/hooks';
import { displayOnHover } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import type { ActivityComment } from './activity-types';

// Card border per the activity-feed design; no divider token exists in appColors yet.
const DIVIDER_COLOR = '#E2E8F0';

const useStyles = makeStyles()(theme => ({
  actionIcon: {
    color: theme.appColors.iconsType,
    opacity: 0.5,
  },
  actions: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.1s ease',
  },
  actor: {
    fontWeight: 600,
  },
  body: {
    color: theme.appColors.iconsType,
  },
  card: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${DIVIDER_COLOR}`,
    borderRadius: 8,
    boxShadow: '0px 0px 3.4px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5),
    width: '100%',
  },
  divider: {
    borderTop: `1px solid ${DIVIDER_COLOR}`,
    width: '100%',
  },
  entry: {
    [`&:hover .${displayOnHover}, &:focus-within .${displayOnHover}`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    minHeight: 22,
  },
  menuIcon: {
    color: theme.appColors.iconsType,
    marginRight: theme.spacing(1),
    opacity: 0.75,
  },
  meta: {
    alignItems: 'baseline',
    columnGap: theme.spacing(1),
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  replyAvatar: {
    flexShrink: 0,
  },
  replyFooter: {
    alignItems: 'center',
    display: 'flex',
  },
  replyInput: {
    color: theme.appColors.iconsType,
    fontSize: 14,
  },
  replyInputRoot: {
    flex: 1,
    gap: theme.spacing(1),
  },
  saveRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  sentence: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    lineHeight: 1.4,
  },
  time: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    opacity: 0.5,
    whiteSpace: 'nowrap',
  },
}));

interface ActivityCommentCardProps {
  /** Feed row this card renders — the anchor a copied link points at. */
  activityItemId: UUID;
  comment: ActivityComment;
  workflowId: UUID;
  /** AI-sourced comment: show the top-level author as "Aline AI" rather than the acting user. */
  isAi?: boolean;
  onSubmitReply?: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
  /** When the anchor is a Slack thread, its header shows a Slack mark + an "open in Slack" action. */
  slackThreadUrl?: string | null;
  /** Origin of the anchor entry — SLACK swaps the header glyph and verb. */
  sourceType?: Apollo.ActivitySourceType;
}

export function ActivityCommentCard({
  activityItemId,
  comment,
  isAi = false,
  onSubmitReply,
  slackThreadUrl,
  sourceType,
  workflowId,
}: ActivityCommentCardProps): JSX.Element | null {
  const { classes } = useStyles();
  const { deleteComment } = useDeleteComment();
  const { deleteCommentReply } = useDeleteCommentReply();
  const [deleted, setDeleted] = useState(false);
  const [deletedReplyIds, setDeletedReplyIds] = useState<UUID[]>([]);

  async function handleDeleteComment() {
    const { data } = await deleteComment(
      comment.id,
      comment.commentable.id,
      comment.commentable.__typename,
      { variables: { input: { id: comment.id } } }
    );
    if (data) setDeleted(true);
  }

  async function handleDeleteReply(replyId: UUID) {
    const { data } = await deleteCommentReply(comment.id, replyId, {
      variables: { input: { id: replyId } },
    });
    if (data) setDeletedReplyIds(prev => [...prev, replyId]);
  }

  if (deleted) return null;

  return (
    <div className={classes.card}>
      <CommentEntry
        activityItemId={activityItemId}
        comment={comment}
        domId={domElIdCreators.commentEl(comment.id)}
        isAi={isAi}
        slackThreadUrl={slackThreadUrl}
        sourceType={sourceType}
        verb={
          sourceType === Apollo.ActivitySourceType.Slack
            ? 'started a Slack thread'
            : 'added a comment'
        }
        workflowId={workflowId}
        onDelete={handleDeleteComment}
      />
      {comment.replies
        .filter(reply => !deletedReplyIds.includes(reply.id))
        .map(reply => (
          <React.Fragment key={reply.id}>
            <div className={classes.divider} />
            <CommentEntry
              activityItemId={activityItemId}
              comment={reply}
              domId={domElIdCreators.commentEl(reply.id)}
              parentComment={comment}
              verb="responded"
              workflowId={workflowId}
              onDelete={() => handleDeleteReply(reply.id)}
            />
          </React.Fragment>
        ))}
      {onSubmitReply && (
        <>
          <div className={classes.divider} />
          <ReplyFooter commentId={comment.id} onSubmitReply={onSubmitReply} />
        </>
      )}
    </div>
  );
}

type ReplyComment = ActivityComment['replies'][number];

interface OptimisticCommentUpdateArgs {
  comment: ActivityComment | ReplyComment;
  text: string;
  parentComment?: ActivityComment;
}

interface CommentEntryProps {
  /**
   * Row this entry belongs to. Copied links anchor on it rather than on the comment, because the
   * feed can seek a window around an activity item — a comment older than the loaded page has no
   * row to scroll to. A reply anchors on its parent's row, which is the card it renders inside.
   */
  activityItemId: UUID;
  comment: ActivityComment | ReplyComment;
  /** DOM id so a freshly-posted reply can be scrolled into view. */
  domId: string;
  onDelete: () => Promise<void>;
  verb: string;
  workflowId: UUID;
  /** AI-sourced comment: display the author as "Aline AI". Replies are never AI. */
  isAi?: boolean;
  /** Root comment used to synthesize a CommentSimple-shaped optimistic reply update. */
  parentComment?: ActivityComment;
  /** Slack deep link — when set, the header shows a Slack mark and an "open in Slack" menu item. */
  slackThreadUrl?: string | null;
  /** Origin of the entry — SLACK swaps the header glyph. */
  sourceType?: Apollo.ActivitySourceType;
}

function CommentEntry({
  activityItemId,
  comment,
  domId,
  isAi = false,
  onDelete,
  parentComment,
  slackThreadUrl,
  sourceType,
  verb,
  workflowId,
}: CommentEntryProps): JSX.Element {
  const { classes, theme } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [editing, setEditing] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const dispatchLinkCopied = useDispatchNotification({
    message: 'Comment link copied',
    variant: 'success',
  });
  const dispatchCopyFailed = useDispatchNotification({
    message: "Couldn't copy comment link",
    variant: 'error',
  });
  const menuOpen = Boolean(menuAnchorEl);
  const actorName = isAi
    ? 'Aline AI'
    : comment.author
      ? getAssigneeDisplayDetails(comment.author).primary
      : (comment.importedAuthorName ?? 'Someone');
  const text = comment.text.trim();
  const canEdit = Boolean(currentUser && !isAi && comment.author?.id === currentUser.id);

  function handleCopyLink(): void {
    const url = new URL(window.location.href);
    url.hash = getActivityAnchorId(activityItemId);
    navigator.clipboard
      .writeText(url.toString())
      .then(dispatchLinkCopied)
      .catch(dispatchCopyFailed);
  }

  return (
    <div className={classes.entry} id={domId}>
      <div className={classes.header}>
        {sourceType === Apollo.ActivitySourceType.Slack ? (
          <SlackIcon height={16} style={{ flexShrink: 0 }} width={16} />
        ) : (
          <MessageCircleMore color={theme.appColors.primary} size={16} style={{ flexShrink: 0 }} />
        )}
        <div className={classes.meta}>
          <Typography className={classes.sentence} component="span" variant="body2">
            <span className={classes.actor}>{actorName}</span> {verb}
          </Typography>
          <Typography className={classes.time} component="span" variant="caption">
            {formatRelativeTime(comment.createdAt)}
          </Typography>
        </div>
        <IconButton
          aria-label="Comment options"
          className={`${classes.actions} ${displayOnHover}`}
          size="small"
          onClick={event => setMenuAnchorEl(event.currentTarget)}
        >
          <MoreHorizontal className={classes.actionIcon} size={16} />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          open={menuOpen}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          onClose={() => setMenuAnchorEl(null)}
        >
          {canEdit && (
            <MenuItem
              dense
              onClick={() => {
                setMenuAnchorEl(null);
                setEditing(true);
              }}
            >
              <Pencil className={classes.menuIcon} size={16} />
              Edit
            </MenuItem>
          )}
          <MenuItem
            dense
            onClick={() => {
              setMenuAnchorEl(null);
              void onDelete();
            }}
          >
            <Trash2 className={classes.menuIcon} size={16} />
            Delete
          </MenuItem>
          <MenuItem
            dense
            onClick={() => {
              setMenuAnchorEl(null);
              handleCopyLink();
            }}
          >
            <LinkIcon className={classes.menuIcon} size={16} />
            Copy link to comment
          </MenuItem>
          {slackThreadUrl && (
            <MenuItem
              dense
              onClick={() => {
                setMenuAnchorEl(null);
                openLink(slackThreadUrl);
              }}
            >
              <ExternalLink className={classes.menuIcon} size={16} />
              View in Slack
            </MenuItem>
          )}
        </Menu>
      </div>
      {editing ? (
        <CommentMarkdownEditor
          comment={comment}
          initialMarkdown={text}
          parentComment={parentComment}
          workflowId={workflowId}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : (
        <div className={classes.body}>
          <AlineMarkdownEditor
            hideToolbar
            noBorder
            readOnly
            maxHeight="none"
            minHeight={0}
            value={text}
          />
        </div>
      )}
    </div>
  );
}

function CommentMarkdownEditor({
  comment,
  initialMarkdown,
  onCancel,
  onSaved,
  parentComment,
  workflowId,
}: {
  comment: ActivityComment | ReplyComment;
  initialMarkdown: string;
  onCancel: () => void;
  onSaved: () => void;
  workflowId: UUID;
  parentComment?: ActivityComment;
}) {
  const { classes } = useStyles();
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [updateComment, { loading }] = Apollo.useUpdateCommentMutation();
  const pristine = markdown === initialMarkdown;

  async function handleSave() {
    const text = normalizeBlankMarkdown(markdown.trim());
    if (!text) return;

    await updateComment({
      optimisticResponse: getOptimisticUpdateCommentResponse({
        comment,
        parentComment,
        text,
      }),
      variables: {
        input: {
          id: comment.id,
          props: { text },
        },
      },
    });
    onSaved();
  }

  return (
    <div>
      <AlineMarkdownEditor
        autoFocus
        hideToolbar
        maxHeight={180}
        mentions={{ workflowId }}
        minHeight={72}
        placeholder="Edit comment..."
        style={{ borderRadius: 8 }}
        value={initialMarkdown}
        onChange={setMarkdown}
        onSubmit={handleSave}
      />
      <div className={classes.saveRow}>
        <SmallAlineButton
          color="primary"
          disabled={pristine || loading || !normalizeBlankMarkdown(markdown.trim())}
          loading={loading}
          variant="contained"
          onClick={handleSave}
        >
          Save
        </SmallAlineButton>
        <SmallAlineButton color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </SmallAlineButton>
      </div>
    </div>
  );
}

function getOptimisticUpdateCommentResponse({
  comment,
  parentComment,
  text,
}: OptimisticCommentUpdateArgs): Apollo.UpdateCommentMutation {
  return {
    __typename: 'Mutation',
    updateComment: {
      __typename: 'CommentResponse',
      comment: getOptimisticUpdatedComment({ comment, parentComment, text }),
    },
  };
}

function getOptimisticUpdatedComment({
  comment,
  parentComment,
  text,
}: OptimisticCommentUpdateArgs): NonNullable<
  Apollo.UpdateCommentMutation['updateComment']['comment']
> {
  if (getIsActivityComment(comment)) {
    return {
      ...comment,
      text,
    };
  }
  if (!parentComment)
    throw new Error('Reply comments require a parent comment for optimistic updates.');

  return {
    ...comment,
    commentable: parentComment.commentable,
    deletedAt: null,
    parent: {
      __typename: 'Comment',
      id: parentComment.id,
    },
    replies: [],
    text,
  };
}

function getIsActivityComment(comment: ActivityComment | ReplyComment): comment is ActivityComment {
  return 'commentable' in comment;
}

function ReplyFooter({
  commentId,
  onSubmitReply,
}: {
  commentId: UUID;
  onSubmitReply: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
}) {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!currentUser) return null;

  async function handleSubmit() {
    const trimmed = replyText.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      const result = await onSubmitReply(commentId, trimmed);
      if (result) setReplyText('');
    } catch {
      // Keep the draft; the global Apollo errorLink surfaces the failure.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={classes.replyFooter}>
      <InputBase
        className={classes.replyInputRoot}
        classes={{ input: classes.replyInput }}
        disabled={submitting}
        placeholder="Leave a reply..."
        startAdornment={
          <AlineAvatar
            avatarInfo={currentUser}
            className={classes.replyAvatar}
            styles={{ size: 'sm' }}
          />
        }
        value={replyText}
        onChange={event => setReplyText(event.target.value)}
        onKeyDown={getOnEnterHandler(handleSubmit)}
      />
    </div>
  );
}
